'use client'

import { cn } from '@/lib/tailwind'
import { CaretDownIcon, XIcon } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'

// Filter dropdown for multi-select options
export function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  icon: Icon,
}: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (selected: string[]) => void
  icon?: React.ElementType
}) {
  const hasSelection = selected.length > 0

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  if (options.length === 0) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex h-6 items-center gap-1 rounded px-2 text-xs font-medium transition-colors',
            hasSelection
              ? 'bg-foreground/10 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
          )}
        >
          {Icon && <Icon className="h-3 w-3" />}
          {hasSelection ? `${label} (${selected.length})` : label}
          <CaretDownIcon className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1.5" align="start">
        <div className="space-y-0.5">
          {options.map((option) => {
            const isSelected = selected.includes(option.value)
            return (
              <button
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className={cn(
                  'flex w-full items-center rounded px-2 py-1.5 text-left text-xs transition-colors',
                  isSelected
                    ? 'bg-foreground/10 text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                )}
              >
                {option.label}
              </button>
            )
          })}
          {hasSelection && (
            <>
              <div className="bg-border my-1 h-px" />
              <button
                onClick={() => onChange([])}
                className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs transition-colors"
              >
                <XIcon className="h-3 w-3" />
                Clear
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
